/**
 * packages.service.ts
 *
 * Typed API call wrappers for the Packages domain (templates).
 * All methods use the shared apiClient (Axios instance with JWT interceptor).
 * Returns unwrapped data from the response envelope — callers never see Axios wrappers.
 */
import { apiClient } from './apiClient';
import type { ApiResponse } from '@/types/api.types';

// ─── Domain types (aligned with server packages.types.ts) ────────────────────

export interface PackageTemplate {
  packageId: number;
  accountId: number;
  name: string;
  description: string | null;
  price: number;
  active: boolean;
  items: PackageItem[];
}

export interface PackageItem {
  packageItemId: number;
  packageId: number;
  itemType?: 'product' | 'procedure';
  procId: number | null;
  procSubId: number | null;
  procName: string | null;
  quantity: number;
  itemName: string;
  expireDays?: number;
  expireMonths?: number;
}

// ─── Payload types ──────────────────────────────────────────────────────────

export interface CreatePackageItemPayload {
  itemType?: 'product' | 'procedure';
  itemName: string;
  quantity: number;
  procId?: number;
  procSubId?: number;
}

export interface CreatePackagePayload {
  name: string;
  description?: string;
  price: number;
  active: boolean;
  items: CreatePackageItemPayload[];
}

export interface UpdatePackagePayload {
  name: string;
  description: string | null;
  price: number;
  active: boolean;
  items?: CreatePackageItemPayload[];
}

// ─── Patient-package types ──────────────────────────────────────────────────

export interface PatientPackageItem {
  patientPackageItemId: number;
  patientPackageId: number;
  packageItemId: number;
  itemType: 'product' | 'procedure';
  itemName: string;
  procId: number | null;
  procSubId?: number | null;
  procName: string | null;
  /** Procedure rows only: the catalog names, as legacy's package list shows them. */
  categoryName?: string | null;
  subcategoryName?: string | null;
  procedureName?: string | null;
  quantity: number;
  usesTotal: number;
  usesConsumed: number;
  usesReturned: number;
  usesRemaining: number;
  paymentId?: number | null;
  usedDate?: string | null;
  deleteDate?: string | null;
  expireDate?: string | null;
  canUse?: boolean;
  canReturn?: boolean;
  canDelete?: boolean;
}

export interface PatientPackageGiftCard {
  giftCardId: number;
  serialNumber: string | null;
  title: string | null;
  redeemedAmount: number;
}

export interface PatientPackage {
  patientPackageId: number;
  patientId: number;
  packageId: number;
  packageName: string;
  planId: number | null;
  quoteId?: number | null;
  facilityId?: number | null;
  paymentId?: number | null;
  paymentMethodTitle?: string | null;
  merchantId?: number | null;
  merchantTitle?: string | null;
  selectedCreditId?: number | null;
  creditAmount?: number;
  chargeAmount?: number;
  giftCards?: PatientPackageGiftCard[];
  purchaseDate: string | null;
  price: number;
  /** Net (post-discount) price actually charged for this package. */
  netPrice?: number;
  discount1Id?: number | null;
  discount2Id?: number | null;
  manualDiscount1?: number;
  manualDiscount2?: number;
  discountTotal?: number;
  notes: string | null;
  active: boolean;
  declined?: boolean;
  items: PatientPackageItem[];
}

export interface CreatePatientPackagePayload {
  packageId: number;
  planId?: number;
  quoteId?: number;
  facilityId?: number;
  purchaseDate?: string;
  price?: number;
  notes?: string;
  merchantId?: number;
  paymentMethodID?: number;
  paymentMethodToken?: string;
  paymentMethodTitle?: string;
  selectedCreditId?: number;
  creditAmount?: number;
  giftCards?: Array<{ giftCardId: number; amount: number }>;
  /** Forward payment_intent id (pi_*), for the intent bridge. Terminal path only. */
  chargeIntentId?: string;
  /** Forward payment id (pmt_*), stored as the transaction id. Terminal path only. */
  chargeTransactionId?: string;
  chargeAmount?: number;
  manualPayment?: boolean;
  referenceNumber?: string;
}

/** One package line in a multi-package (batch) purchase. `price` is the GROSS
 *  template price; the server applies the discounts and stores the net. */
export interface CreatePatientPackagesBatchItem {
  packageId: number;
  price?: number;
  discount1Id?: number;
  manualDiscount1?: number;
  discount2Id?: number;
  manualDiscount2?: number;
}

/** Multiple packages enrolled under ONE payment / one combined charge. Mirrors
 *  the single payload's shared payment/gift-card fields but replaces the single
 *  package with a `packages[]` array. */
export interface CreatePatientPackagesBatchPayload {
  packages: CreatePatientPackagesBatchItem[];
  planId?: number;
  quoteId?: number;
  facilityId?: number;
  purchaseDate?: string;
  notes?: string;
  merchantId?: number;
  paymentMethodID?: number;
  paymentMethodToken?: string;
  paymentMethodTitle?: string;
  selectedCreditId?: number;
  creditAmount?: number;
  giftCards?: Array<{ giftCardId: number; amount: number }>;
  chargeIntentId?: string;
  chargeTransactionId?: string;
  chargeAmount?: number;
  manualPayment?: boolean;
  referenceNumber?: string;
}

export interface CreatePatientPackagesBatchResult {
  patientPackageIds: number[];
  patientId: number;
  paymentId: number | null;
}

export interface ConsumePackageItemPayload {
  itemType?: 'product' | 'procedure';
  disburseQuantity?: number;
}

// ─── Service methods ─────────────────────────────────────────────────────────

/**
 * Fetch all package templates for the current account.
 */
export function getPackageTemplates(): Promise<PackageTemplate[]> {
  return apiClient
    .get<ApiResponse<PackageTemplate[]>>('/packages/templates')
    .then((r) => r.data.data);
}

/**
 * Fetch a single package template by ID, including its items.
 */
export function getPackageTemplate(packageId: number): Promise<PackageTemplate> {
  return apiClient
    .get<ApiResponse<PackageTemplate>>(`/packages/templates/${packageId}`)
    .then((r) => r.data.data);
}

/**
 * Create a new package template with items.
 */
export function createPackageTemplate(data: CreatePackagePayload): Promise<PackageTemplate> {
  return apiClient
    .post<ApiResponse<PackageTemplate>>('/packages/templates', data)
    .then((r) => r.data.data);
}

/**
 * Update a package template, including items when provided.
 */
export function updatePackageTemplate(packageId: number, data: UpdatePackagePayload): Promise<PackageTemplate> {
  return apiClient
    .put<ApiResponse<PackageTemplate>>(`/packages/templates/${packageId}`, data)
    .then((r) => r.data.data);
}

/**
 * Clone (duplicate) a package template.
 */
export function clonePackageTemplate(packageId: number): Promise<PackageTemplate> {
  return apiClient
    .post<ApiResponse<PackageTemplate>>(`/packages/templates/${packageId}/clone`)
    .then((r) => r.data.data);
}

/**
 * Delete a package template.
 */
export function deletePackageTemplate(packageId: number): Promise<void> {
  return apiClient
    .delete(`/packages/templates/${packageId}`)
    .then(() => undefined);
}

// ─── Patient-package service methods ────────────────────────────────────────

/**
 * Fetch all packages enrolled for a patient.
 */
export function getPatientPackages(patientId: number, options: { includeInactive?: boolean } = {}): Promise<PatientPackage[]> {
  return apiClient
    .get<ApiResponse<PatientPackage[]>>(`/patients/${patientId}/packages`, {
      params: options.includeInactive ? { includeInactive: 1 } : undefined,
    })
    .then((r) => r.data.data);
}

/**
 * Enroll a patient in a package template.
 */
export function enrollInPackage(patientId: number, data: CreatePatientPackagePayload): Promise<PatientPackage> {
  return apiClient
    .post<ApiResponse<PatientPackage>>(`/patients/${patientId}/packages`, data)
    .then((r) => r.data.data);
}

/**
 * Enroll a patient in multiple package templates under ONE payment / one
 * combined charge. Backend creates a single payment + N package rows sharing
 * that paymentID (see PackageExtrasController::createPatientPackagesBatch).
 */
export function enrollInPackagesBatch(
  patientId: number,
  data: CreatePatientPackagesBatchPayload,
): Promise<CreatePatientPackagesBatchResult> {
  return apiClient
    .post<ApiResponse<CreatePatientPackagesBatchResult>>(`/patients/${patientId}/packages/batch`, data)
    .then((r) => r.data.data);
}

/**
 * Consume one use of a patient package item.
 */
export function consumeUse(patientPackageItemId: number, data?: ConsumePackageItemPayload): Promise<void> {
  return apiClient
    .post(`/packages/items/${patientPackageItemId}/consume`, data ?? {})
    .then(() => undefined);
}

/**
 * Return one use of a patient package item.
 */
export function returnUse(patientPackageItemId: number, data?: Pick<ConsumePackageItemPayload, 'itemType'>): Promise<void> {
  return apiClient
    .post(`/packages/items/${patientPackageItemId}/return`, data ?? {})
    .then(() => undefined);
}

export function deletePackageItem(
  patientPackageItemId: number,
  data?: Pick<ConsumePackageItemPayload, 'itemType'>,
): Promise<void> {
  return apiClient
    .post(`/packages/items/${patientPackageItemId}/delete`, data ?? {})
    .then(() => undefined);
}

export function deletePatientPackage(patientPackageId: number): Promise<void> {
  return apiClient
    .post(`/packages/${patientPackageId}/delete`)
    .then(() => undefined);
}

export interface AddPackageItemPayload {
  productId: number;
  quantity?: number;
  expireDate?: string;
}

/**
 * Add an ad-hoc product to an existing patient package. Package-side analogue
 * of addEnrollmentItem for memberships; inserts one packages_products row.
 */
export function addPackageItem(
  packageId: number,
  payload: AddPackageItemPayload,
): Promise<void> {
  return apiClient
    .post(`/packages/${packageId}/items`, { itemType: 'product', ...payload })
    .then(() => undefined);
}
